import * as React from "react";

import "./support.scss";

import {faComment, faEnvelope, faPhone}     from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon}                    from "@fortawesome/react-fontawesome";
import {cx}                                 from "emotion";

import {EMAIL_REGEX}                        from "@Common/react/cs-form/validators/email-format";
import {
    ComponentLink,
    Link,
    makeChain,
    valueHelper as value,
}                                           from "@Common/react/cst2";
import {consumeContext as contextCommon}    from "@Common/react/context";
import {scopedState}                        from "@Common/react/scoped-state";
import {UseState}                           from "@Common/react/use-state";
import {Types}                              from "@Common/types";
import {AlertModal}                         from "@Common/ui-components/heavy/common/design2/dialogs/alert-modal/alert-modal";
import {ReCaptcha}                          from "@Common/ui-components/recaptcha/recaptcha";
import {chain}                              from "@Common/utils/fs";

import {consumeContext}                     from "@Xpresso/loaders/consume-context";


import {SubHeader}                          from "../common/sub-header/sub-header";
import {Layout}                             from "../layout/layout";

declare global {
    interface Window {
        onCaptchaLoad   ?: () => void,
        fbAsyncInit     ?: () => void,
    }
}

type ApiRequest = Types.Apis.Payloads.Next.ContactUs.PostContactUs.Request;

export const SupportRoute = () => (
    makeChain<JSX.Element>()
    .then(...consumeContext("themeScheme"))
    .then(({
        themeScheme,
    }) => (
    <Layout>
        <SubHeader
            backLink={themeScheme.type === "loggedIn" ? {to: "/", label: "Home"} : {}}
            title="Contact Us"
        />
        <Support/>
    </Layout>
    ))
    .run()
);

const Support = () => (
    makeChain<JSX.Element>()
    .then(...consumeContext("apis"))
    .then(...consumeContext("company"))
    .then(...consumeContext("webchat"))
    .then(...contextCommon("liveChatConfig"))
    .then("helpPhone", ({}, next:ComponentLink<typeof HelpPhoneProvider, "renderChildren">) => HelpPhoneProvider({renderChildren: next}))

    .then("formState", ({company}, next:ComponentLink<typeof UseState<Partial<ApiRequest>>>) => (
        UseState({next, initValue: {companyName: company?.companyName}})
    ))
    .then("formError", value(({formState: {value: formValue}}) => (
        !formValue.captcha
            ? "Please complete the ReCAPTCHA challenge"
            : FORM_FIELDS.reduce((previousError, {key, label, validators}) => {
                if (previousError) {
                    return previousError;
                }

                const fieldValue        = formValue[key];
                const failingValidator  = validators?.find(({filter}) => !filter(fieldValue));
                const error             = failingValidator?.makeError({value: fieldValue});
                return error ? `${label}: ${error}` : null;
            }, null as string)
    )))

    .then("submittingState", ({}, next:ComponentLink<typeof UseState<boolean>>) => UseState({next}))
    .then("successDialog", ({}, next:ComponentLink<typeof AlertModal>) => AlertModal({next}))

    .then("captchaTokenState", value(({formState}) => scopedState(formState, ["captcha"])))
    .then(({
        captchaTokenState: {onChange: setCaptchaToken},
        company,
        liveChatConfig,
        helpPhone,
        formError,
        formState,
        submittingState: {value: submitting},
        webchat,
    }, next:Link<void>) => (
        <main className="support-880cf">
            <header><span className="title">Get in touch!</span></header>
            <section>
                <p>
                { contactMessage(liveChatConfig) }
                </p>
                <ul className="contact-options">
                    {company?.prefs.enableLiveChat !== false && <li><a onClick={webchat.show}><FontAwesomeIcon icon={faComment} className="icon"/> Live Chat</a></li>}
                    <li><a href={helpPhone.url}><FontAwesomeIcon icon={faPhone} className="icon"/> {helpPhone.label}</a></li>
                    <li><FontAwesomeIcon icon={faEnvelope} className="icon"/> Email us via the form below</li>
                </ul>
            </section>
            <section>
                <p>Please fill out the following fields and a support representative will respond to assist you within 24 hours.</p>
                {FORM_FIELDS.map(({inputMaker: Input, label, key, validators}) => (
                    chain(
                        scopedState(formState, [key]),
                        ({onChange: setFieldValue, value: fieldValue}) => (
                            <div key={key} className={cx("input-field", {required: validators?.includes(VALIDATORS.required)})}>
                                <label>{label}</label>
                                <Input
                                    disabled={submitting || (key === "companyName" && !!company)}
                                    label={label}
                                    onChange={setFieldValue}
                                    value={fieldValue ?? ""}
                                />
                            </div>
                        )
                    )
                ))}
                <ReCaptcha onExpiration={() => setCaptchaToken(null)} onVerification={setCaptchaToken}/>
                <button
                    disabled={submitting || !!formError}
                    onClick={next}
                    xd-qa-type="button"
                    xd-qa-name="submit support request"
                >
                    Submit support request
                </button>
                {formError && <em className="error">{formError}</em>}
            </section>
        </main>
    ))

    // onSubmit
    .then(async ({apis, formState: {value: formValue}, submittingState: {onChange: setSubmitting}, successDialog}) => {
        setSubmitting(true);
        await apis.next.contactUs.submitSupportRequest(formValue as ApiRequest);
        successDialog.show({message: "Thank you for submitting your support request. A support representative will contact you within 24 hours to assist."});
        setSubmitting(false);
    })
    .run()
);

const HelpPhoneProvider = ({renderChildren}:{
    renderChildren  : Link<JSX.Element, {label: string, url: string}>,
}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("menus"))
    .then(...consumeContext("themeScheme"))
    .then(({menus, themeScheme}) => (
        renderChildren(
            chain(
                menus?.helpMenu ?? themeScheme?.helpMenu,
                (helpMenu) => helpMenu?.subMenu?.find(({type}) => type === "tel"),
                (tel) => ({
                    label   : tel?.linkLabel ?? "?",
                    url     : tel?.link ?? "#",
                })
            )
        )
    ))
    .run()
);

// FIELDS

const ASSISTANCE_TOPIC_OPTIONS = [
    "Address List Assistance",
    "Billing Question",
    "Cancellation Request",
    "Username / Password Request",
    "Order Assistance",
];

interface FieldValidator {
    filter      : (value:string) => boolean,
    makeError   : (context:{value:string}) => string,
}

interface InputProps {
    disabled    ?: boolean,
    label        : string,
    onChange     : (value:string) => void,
    value        : string,
}

const CategoryOptionsInput = ({disabled, label, onChange: emitChange, value}:InputProps) => (
    <select
        {...{disabled, value}}
        onChange={(e) => emitChange(e.target.value)}
        xd-qa-type="dropdown"
        xd-qa-name={label}
    >
        <option disabled></option>
        {ASSISTANCE_TOPIC_OPTIONS.map((text, index) => (
            <option 
                key={index} 
                value={text}
                xd-qa-type="dropdown_item"
                xd-qa-name={text}
            >
                {text}
            </option>
        ))}
    </select>
);

const SimpleInput = ({disabled, label, onChange: emitChange, type, value}:InputProps & {type?: string}) => (
    <input
        {...{disabled, type, value}}
        onChange={(e) => emitChange(e.target.value)}
        xd-qa-type="field"
        xd-qa-name={label}
    />
);

const TextareaInput = ({disabled, onChange: emitChange, label, value}:InputProps) => (
    <textarea
        {...{disabled, value}}
        onChange={(e) => emitChange(e.target.value)}
        rows={4}
        xd-qa-type="field"
        xd-qa-name={label}
    />
);

const VALIDATORS:Record<string, FieldValidator> = {
    email       : {
        filter      : (value) => EMAIL_REGEX.test(value),
        makeError   : () => "Please enter a valid email address",
    },
    phone       : {
        filter      : (value) => !value || value.replace(/\D/g, "").length > 4,
        makeError   : () => "Please enter a valid phone number",
    },
    required    : {
        filter      : (value) => !!value,
        makeError   : () => "This field is required",
    },
};

const FORM_FIELDS:Array<{
    inputMaker  ?: (props:InputProps) => JSX.Element,
    key          : keyof ApiRequest,
    label        : string,
    validators  ?: Array<FieldValidator>,
}> = [
    {key: "companyName",   inputMaker: SimpleInput,                                        label: "Company",                        validators: [VALIDATORS.required]},
    {key: "firstName",     inputMaker: SimpleInput,                                        label: "First Name",                     validators: [VALIDATORS.required]},
    {key: "lastName",      inputMaker: SimpleInput,                                        label: "Last Name",                      validators: [VALIDATORS.required]},
    {key: "emailaddress",  inputMaker: (props) => SimpleInput({...props, type: "email"}),  label: "Email Address",                  validators: [VALIDATORS.required, VALIDATORS.email]},
    {key: "phoneNumber",   inputMaker: (props) => SimpleInput({...props, type: "phone"}),  label: "Phone Number",                   validators: [VALIDATORS.phone]},
    {key: "orderNumber",   inputMaker: SimpleInput,                                        label: "Order Number (if applicable)"},
    {key: "category",      inputMaker: CategoryOptionsInput,                               label: "How can we assist?",             validators: [VALIDATORS.required]},
    {key: "comments",      inputMaker: TextareaInput,                                      label: "Additional Comments"},
];

const contactMessage = (liveChatConfig) => {
    const startDefault = '?'
    const endDefault = '?'
    const start = liveChatConfig?.contactCenterStart ? militaryToStandard(liveChatConfig?.contactCenterStart) : startDefault
    const end = liveChatConfig?.contactCenterEnd ? militaryToStandard(liveChatConfig?.contactCenterEnd) : endDefault

    return `If you have a question or require assistance, feel free to contact one of our support representatives. We are available Monday through Friday from ${start} - ${end} CST.`
}

const militaryToStandard = (time) => {
    const hoursMinutes = time.split(':')

    const hours = parseInt(hoursMinutes[0]) || 0
    const hoursStringified = (0 == hours || 12 == hours)
        ? `12`
        : `${hours % 12}`

    const minutes = parseInt(hoursMinutes[1]) || 0
    const minutesPadded = minutes < 10 ? `0${minutes}` : `${minutes}`

    const meridiem = hours < 12 ? 'am' : 'pm'

    if('00' == minutesPadded) {
        return `${hoursStringified}${meridiem}`
    }

    return `${hoursStringified}:${minutesPadded}${meridiem}`
}

